import { useEffect, useRef } from "react";
import { useEnvironment } from "../../core/Global";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
    interface Window {
        turnstile?: any;
    }
}

// Loads the Turnstile script once per page and resolves when it is ready. Several widgets (or a
// remount) share the same promise instead of injecting the script again.
let scriptPromise: Promise<void> | undefined;

function loadScript(): Promise<void> {
    if (window.turnstile) return Promise.resolve();
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () => reject());
            return;
        }

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            scriptPromise = undefined;
            reject();
        };
        document.head.appendChild(script);
    });

    return scriptPromise;
}


export type TurnstileRef = {
    reset: () => void;
};

interface IProps {
    // Fires with the token once the check passes, and with undefined when it expires or errors,
    // so the parent can keep the submit button in step with the widget.
    onToken: (token?: string) => void
    onRef?: (ref: TurnstileRef) => void
    className?: string
}

export default function Turnstile(props: IProps) {

    const env = useEnvironment();
    const container = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string>();

    // Held in a ref so the render callback always calls the latest handler without re-rendering
    // the widget, which would reset the challenge on every parent update.
    const onToken = useRef(props.onToken);
    onToken.current = props.onToken;

    useEffect(() => {

        let cancelled = false;

        // No key configured (local work against an env.json without one): report a token-less pass
        // so the form stays usable. The HUB is what decides whether a token is required.
        if (!env.turnstileSiteKey) {
            onToken.current(undefined);
            return;
        }

        loadScript()
            .then(() => {
                if (cancelled || !container.current || !window.turnstile) return;

                widgetId.current = window.turnstile.render(container.current, {
                    sitekey: env.turnstileSiteKey,
                    callback: (token: string) => onToken.current(token),
                    "expired-callback": () => onToken.current(undefined),
                    "error-callback": () => onToken.current(undefined)
                });

                props.onRef?.({
                    reset: () => {
                        if (widgetId.current) window.turnstile?.reset(widgetId.current);
                        onToken.current(undefined);
                    }
                });
            })
            .catch(() => {
                // Cloudflare unreachable: leave the widget empty rather than blocking the form here.
                // The HUB still refuses a signup without a valid token.
                if (!cancelled) onToken.current(undefined);
            });

        return () => {
            cancelled = true;
            if (widgetId.current) {
                try { window.turnstile?.remove(widgetId.current); } catch { }
                widgetId.current = undefined;
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={container} className={props.className} />;
}
